import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImportBookkeepingDocumentsDrawer } from './ImportBookkeepingDocumentsDrawer';
import { PrimaryButton } from '../../../../../../../components/general/PrimaryButton';
import { useNotifications } from '../../../../../../../lib/NotificationContext';
import {
    ArrowPathIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { Tag } from '../../../../../../../components/general/Tag';
import { BKSDocumentImportState } from './useBKSDocumentImport';
import {
    BKSEntrepreneurBusiness,
    BKSImportJobSummary,
    BKSImportParams,
    BKSImportStatusResponse,
    BKSJournal,
    BKS_IMPORT_JOB_ERROR_FALLBACK,
    formatBKSImportErrorContext,
    resolveBKSImportJobErrorMessage,
} from './bksDocumentImport.types';

type DocumentImportSectionProps = BKSDocumentImportState & {
    hideHeader?: boolean;
};

function formatDateRange(dateFrom: string, dateTo: string): string {
    if (dateFrom === dateTo) {
        return dateFrom;
    }
    return `${dateFrom} – ${dateTo}`;
}

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function isActiveStatus(status: string): boolean {
    return status === 'PENDING' || status === 'RUNNING';
}

function renderStatusTag(status: string) {
    switch (status) {
        case 'PENDING':
        case 'RUNNING':
            return <Tag variant="primary" size="xs">Bezig</Tag>;
        case 'PROCESSED':
            return <Tag variant="success" size="xs">Voltooid</Tag>;
        case 'FAILED':
            return <Tag variant="error" size="xs">Mislukt</Tag>;
        default:
            return <Tag variant="default" size="xs">{status}</Tag>;
    }
}

function ImportJobProgressCard({
    status,
    showFailedDetails,
    onToggleFailedDetails,
}: {
    status: BKSImportStatusResponse;
    showFailedDetails: boolean;
    onToggleFailedDetails: () => void;
}) {
    const total = status.total_documents || 0;
    const processed = status.processed_documents || 0;
    const failed = status.failed_documents || 0;
    const completed = processed + failed;
    const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
    const isActive = isActiveStatus(status.status);
    const isCompleted = status.status === 'PROCESSED';
    const isFailed = status.status === 'FAILED';
    const hasPartialFailures = (isCompleted || isFailed) && failed > 0;
    const failedDetails = status.failed_document_details || [];
    const jobErrorMessage = formatBKSImportErrorContext(status.error_context);
    const showJobLevelError = Boolean(jobErrorMessage) && (isFailed || (isCompleted && hasPartialFailures));

    return (
        <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 overflow-hidden">
            <div className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {isActive && <ArrowPathIcon className="h-4 w-4 animate-spin text-primary" />}
                    {isCompleted && !hasPartialFailures && <CheckCircleIcon className="h-4 w-4 text-green-600" />}
                    {isCompleted && hasPartialFailures && <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />}
                    {isFailed && <XCircleIcon className="h-4 w-4 text-red-600" />}
                    <span className="text-sm text-gray-700">
                        {isActive && 'Import wordt verwerkt'}
                        {isCompleted && !hasPartialFailures && 'Import voltooid'}
                        {isCompleted && hasPartialFailures && 'Import afgerond met fouten'}
                        {isFailed && 'Import mislukt'}
                    </span>
                </div>
                {renderStatusTag(status.status)}
            </div>

            {isActive && (
                <div className="px-4 pb-3">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">{completed} van {total} documenten</span>
                        <span className="text-xs font-medium text-gray-700">{progressPercent}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                        <div
                            className="h-1.5 rounded-full transition-all duration-700 ease-in-out bg-primary"
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                </div>
            )}

            {(isCompleted || isFailed) && (
                <div className="px-4 pb-3 grid grid-cols-3 gap-3">
                    <div>
                        <span className="text-xs text-gray-500">Totaal</span>
                        <p className="text-sm font-semibold text-gray-900">{total}</p>
                    </div>
                    <div>
                        <span className="text-xs text-gray-500">Succesvol</span>
                        <p className="text-sm font-semibold text-green-600">{processed}</p>
                    </div>
                    <div>
                        <span className="text-xs text-gray-500">Mislukt</span>
                        <p className={`text-sm font-semibold ${failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>{failed}</p>
                    </div>
                </div>
            )}

            {isFailed && !jobErrorMessage && (
                <div className="px-4 pb-3 border-t border-gray-200">
                    <p className="text-xs font-medium text-red-700 pt-2">Foutmelding</p>
                    <p className="text-xs text-red-600 break-words mt-1">{BKS_IMPORT_JOB_ERROR_FALLBACK}</p>
                </div>
            )}

            {showJobLevelError && jobErrorMessage && (
                <div className="px-4 pb-3 border-t border-gray-200">
                    <p className="text-xs font-medium text-red-700 pt-2">Foutmelding</p>
                    <p className="text-xs text-red-600 break-words mt-1">{jobErrorMessage}</p>
                </div>
            )}

            {hasPartialFailures && failedDetails.length > 0 && (
                <div className="border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onToggleFailedDetails}
                        className="w-full px-4 py-2 flex items-center justify-between text-xs text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                        <span>{failedDetails.length} mislukte document{failedDetails.length !== 1 ? 'en' : ''} bekijken</span>
                        {showFailedDetails ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
                    </button>
                    {showFailedDetails && (
                        <div className="px-4 pb-3 max-h-40 overflow-y-auto space-y-2">
                            {failedDetails.map((err, index) => (
                                <div key={index} className="flex items-start gap-2 text-xs">
                                    <XCircleIcon className="h-3.5 w-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <span className="font-medium text-gray-700">
                                            {err.document_id || err.invoice_id || `Document ${index + 1}`}
                                        </span>
                                        <p className="text-gray-500 break-words">{err.error || 'Onbekende fout'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ImportJobRow({
    job,
    detailedStatus,
    isExpanded,
    showFailedDetails,
    onToggleExpand,
    onToggleFailedDetails,
}: {
    job: BKSImportJobSummary;
    detailedStatus: BKSImportStatusResponse | null;
    isExpanded: boolean;
    showFailedDetails: boolean;
    onToggleExpand: () => void;
    onToggleFailedDetails: () => void;
}) {
    const failedJobErrorMessage = job.status === 'FAILED'
        ? resolveBKSImportJobErrorMessage(detailedStatus?.error_context, job.error_context)
        : null;

    return (
        <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
            <button
                type="button"
                onClick={onToggleExpand}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
                <div className="min-w-0 pr-4">
                    <p className="text-sm font-medium text-gray-900 truncate">
                        {job.connection_name || 'Boekhoudkoppeling'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {formatDateRange(job.date_from, job.date_to)} · {formatDateTime(job.created_at)}
                    </p>
                    {job.status === 'FAILED' && failedJobErrorMessage && (
                        <p className="text-xs text-red-600 mt-1 break-words line-clamp-2">{failedJobErrorMessage}</p>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {renderStatusTag(job.status)}
                    {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                </div>
            </button>
            {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                    {detailedStatus ? (
                        <ImportJobProgressCard
                            status={detailedStatus}
                            showFailedDetails={showFailedDetails}
                            onToggleFailedDetails={onToggleFailedDetails}
                        />
                    ) : (
                        <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                            <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                                <ArrowPathIcon className="h-4 w-4 animate-spin text-primary" />
                                Importdetails laden...
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export function DocumentImportSection({
    hasIntegration,
    connections,
    importJobs,
    isLoadingJobs,
    onLoadBKSAdministrations,
    onLoadBKSJournals,
    onInitiateBKSImport,
    onPollBKSImportStatus,
    onRefreshImportJobs,
    getConnectionMapping,
    hideHeader = false,
}: DocumentImportSectionProps) {
    const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
    const [administrations, setAdministrations] = useState<BKSEntrepreneurBusiness[]>([]);
    const [journals, setJournals] = useState<BKSJournal[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [expandedImportId, setExpandedImportId] = useState<string | null>(null);
    const [showFailedDetailsForImportId, setShowFailedDetailsForImportId] = useState<string | null>(null);
    const [jobStatuses, setJobStatuses] = useState<Record<string, BKSImportStatusResponse>>({});
    const hasAutoExpandedInitialJobRef = useRef(false);
    const { dispatch } = useNotifications();

    const activeImportIds = useMemo(
        () => importJobs.filter((job) => isActiveStatus(job.status)).map((job) => job.import_id),
        [importJobs],
    );

    const sortedConnections = useMemo(
        () => [...connections].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        [connections],
    );

    const hasActiveJobForConnection = (connectionId: string | null) => {
        if (!connectionId) {
            return false;
        }
        return importJobs.some(
            (job) => job.connection_id === connectionId && isActiveStatus(job.status),
        );
    };

    useEffect(() => {
        if (sortedConnections.length > 0 && !selectedConnectionId) {
            setSelectedConnectionId(sortedConnections[0].id);
        }
    }, [sortedConnections, selectedConnectionId]);

    const loadAdministrations = useCallback(async (connectionId: string) => {
        setAdministrations([]);
        setJournals([]);
        try {
            const result = await onLoadBKSAdministrations(connectionId);
            setAdministrations(result);
        } catch {
            setAdministrations([]);
        }
    }, [onLoadBKSAdministrations]);

    useEffect(() => {
        if (selectedConnectionId) {
            void loadAdministrations(selectedConnectionId);
        }
    }, [selectedConnectionId, loadAdministrations]);

    useEffect(() => {
        if (importJobs.length > 0 && !hasAutoExpandedInitialJobRef.current) {
            hasAutoExpandedInitialJobRef.current = true;
            setExpandedImportId(importJobs[0].import_id);
        }
    }, [importJobs]);

    useEffect(() => {
        if (activeImportIds.length === 0) {
            return;
        }

        let cancelled = false;

        async function refreshActiveStatuses() {
            const results = await Promise.allSettled(
                activeImportIds.map((importId) => onPollBKSImportStatus(importId)),
            );

            if (cancelled) {
                return;
            }

            const nextStatuses: Record<string, BKSImportStatusResponse> = {};
            let shouldRefreshJobs = false;

            results.forEach((result, index) => {
                const importId = activeImportIds[index];
                if (result.status === 'fulfilled') {
                    nextStatuses[importId] = result.value;
                    if (!isActiveStatus(result.value.status)) {
                        shouldRefreshJobs = true;
                    }
                }
            });

            setJobStatuses((current) => ({ ...current, ...nextStatuses }));

            if (shouldRefreshJobs) {
                await onRefreshImportJobs();
            }
        }

        void refreshActiveStatuses();

        const interval = setInterval(() => {
            void refreshActiveStatuses();
        }, 10000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [activeImportIds, onPollBKSImportStatus, onRefreshImportJobs]);

    useEffect(() => {
        if (!expandedImportId || jobStatuses[expandedImportId]) {
            return;
        }

        const summary = importJobs.find((job) => job.import_id === expandedImportId);
        if (!summary || isActiveStatus(summary.status)) {
            return;
        }

        let cancelled = false;
        let retryTimeout: ReturnType<typeof setTimeout> | undefined;

        const loadDetails = async () => {
            try {
                const status = await onPollBKSImportStatus(expandedImportId);
                if (cancelled) {
                    return;
                }
                setJobStatuses((current) => ({ ...current, [expandedImportId]: status }));
            } catch {
                if (cancelled) {
                    return;
                }
                retryTimeout = setTimeout(() => {
                    void loadDetails();
                }, 3000);
            }
        };

        void loadDetails();

        return () => {
            cancelled = true;
            if (retryTimeout) {
                clearTimeout(retryTimeout);
            }
        };
    }, [expandedImportId, importJobs, jobStatuses, onPollBKSImportStatus]);

    const handleAdministrationSelect = useCallback(async (administrationId: string) => {
        setJournals([]);
        if (!selectedConnectionId) {
            return;
        }
        try {
            const data = await onLoadBKSJournals(administrationId, selectedConnectionId);
            setJournals(data);
        } catch {
            dispatch({
                title: 'Fout',
                description: 'Kan de dagboeken niet laden. Probeer het later opnieuw.',
                type: 'error',
            });
        }
    }, [dispatch, onLoadBKSJournals, selectedConnectionId]);

    const handleImportStart = async (params: Omit<BKSImportParams, 'connectionId'> & { connectionId?: string }) => {
        const connectionId = params.connectionId || selectedConnectionId;
        if (!connectionId) {
            throw new Error('No connection selected');
        }

        if (hasActiveJobForConnection(connectionId)) {
            dispatch({
                title: 'Import al actief',
                description: 'Er loopt al een import voor deze koppeling. Wacht tot deze is afgerond.',
                type: 'error',
            });
            throw new Error('Active import for connection');
        }

        try {
            const result = await onInitiateBKSImport({
                ...params,
                connectionId,
            });
            dispatch({
                title: 'Import gestart',
                description: 'De documenten worden op de achtergrond geïmporteerd.',
                type: 'success',
            });
            setExpandedImportId(result.import_id);
        } catch (error: unknown) {
            const detail = typeof error === 'object' && error !== null && 'detail' in error
                ? String((error as { detail: unknown }).detail)
                : null;
            const isConflict = typeof detail === 'string' && detail.toLowerCase().includes('active import');

            dispatch({
                title: 'Fout',
                description: isConflict
                    ? 'Er loopt al een import voor deze koppeling.'
                    : 'Importeren is mislukt. Probeer het opnieuw of neem contact op met support.',
                type: 'error',
            });
            throw error;
        }
    };

    const selectedConnectionHasActiveJob = hasActiveJobForConnection(selectedConnectionId);

    return (
        <div className={hideHeader ? '' : 'mt-8 border-t pt-8'}>
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        {!hideHeader && (
                            <h3 className="text-lg font-medium text-gray-900">
                                Documenten importeren
                            </h3>
                        )}
                        <p className="text-sm text-gray-600">
                            Importeer historische documenten vanuit het gekoppelde boekhoudpakket.
                        </p>
                    </div>
                    <PrimaryButton
                        title="Start import"
                        onClick={() => setDrawerOpen(true)}
                        disabled={hasIntegration !== true}
                        className="flex-shrink-0 whitespace-nowrap"
                        disabledFeedback={
                            hasIntegration === null
                                ? 'Boekhoudkoppelingen laden...'
                                : 'Koppel eerst een boekhoudpakket'
                        }
                    />
                </div>

                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">Importgeschiedenis</h4>
                        <button
                            type="button"
                            onClick={() => void onRefreshImportJobs()}
                            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                        >
                            <ArrowPathIcon className={`h-3.5 w-3.5 ${isLoadingJobs ? 'animate-spin' : ''}`} />
                            Vernieuwen
                        </button>
                    </div>

                    {importJobs.length === 0 && !isLoadingJobs && (
                        <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                            Nog geen imports uitgevoerd voor deze klant.
                        </div>
                    )}

                    {importJobs.map((job) => (
                        <ImportJobRow
                            key={job.import_id}
                            job={jobStatuses[job.import_id]
                                ? { ...job, status: jobStatuses[job.import_id].status }
                                : job}
                            detailedStatus={jobStatuses[job.import_id] ?? null}
                            isExpanded={expandedImportId === job.import_id}
                            showFailedDetails={showFailedDetailsForImportId === job.import_id}
                            onToggleExpand={() => {
                                setExpandedImportId((current) => current === job.import_id ? null : job.import_id);
                            }}
                            onToggleFailedDetails={() => {
                                setShowFailedDetailsForImportId((current) => current === job.import_id ? null : job.import_id);
                            }}
                        />
                    ))}
                </div>
            </div>

            <ImportBookkeepingDocumentsDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                connections={sortedConnections}
                selectedConnectionId={selectedConnectionId}
                onConnectionChange={setSelectedConnectionId}
                bookkeepingAdministrations={administrations}
                journals={journals}
                connectionMapping={selectedConnectionId ? getConnectionMapping(selectedConnectionId) : undefined}
                hasActiveJobForSelectedConnection={selectedConnectionHasActiveJob}
                onAdministrationSelect={handleAdministrationSelect}
                onImportStart={handleImportStart}
            />
        </div>
    );
}
